import {
  AbsoluteFill,
  OffthreadVideo,
  Audio,
  Img,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
} from "remotion";
import { loadFont as loadDev } from "@remotion/google-fonts/NotoSansDevanagari";
import data from "./kamakhya_player_data.json";

const { fontFamily: devFont } = loadDev("normal", {
  weights: ["500", "700"],
  subsets: ["devanagari", "latin"],
});

// ── canvas + card geometry ───────────────────────────────────────────────
const W = 1280;
const H = 704;
// assets on R2 (so the Modal render container can fetch them — no public/ needed)
const BASE_URL = "https://pub-bb3ce5df1bc243da96f744a22c134897.r2.dev/player/kamakhya_base.mp4";
const SONG_URL = "https://pub-bb3ce5df1bc243da96f744a22c134897.r2.dev/audio/979693b6-2b08-4d5b-8664-5503f75bce31_full.mp3";
const LOGO_URL = "https://pub-bb3ce5df1bc243da96f744a22c134897.r2.dev/assets/assets/DevaKathaMusic.png";
const CARD_W = 1045; // -5%
const CARD_H = 574; // -5%
const RADIUS = 30;
const BORDER = 5;
// card resting geometry (centred) — lyrics anchor to its bottom-right, logo to its top-right
const CARD_RIGHT_FROM_EDGE = (W - CARD_W) / 2; // 117.5
const CARD_BOTTOM_FROM_EDGE = (H - CARD_H) / 2; // 65
const CARD_TOP_FROM_EDGE = (H - CARD_H) / 2; // 65 (card is centred)
const LOGO = 60; // 60px wide @ 720p
const LOGO_PAD = 20; // 20px inside the card's top-right corner
const BAR_W = Math.round((CARD_W - 80) * 0.88); // 80% then +10% length, centred
const BAR_BOTTOM = 95; // progress bar distance from canvas bottom (shifted up 5px)
const TITLE_BOTTOM = BAR_BOTTOM + 20.5; // 3px above the playhead triangle top (bar center +15 +3)

// ── beat-punch params (mirror the ffmpeg prototype) ──────────────────────
const ZOOM = 0.1;
const SIGMA = 0.05;
const WMIN = 0.12;
const GAMMA = 1.4;
const STR_THRESH = 0.22;
const MINGAP = 0.28;
const BREATH = 0.008;
const BREATH_T = 8.0;
const FLASH = 0.05;

// precompute strength-weighted, thinned beats once at module load
const sMin = Math.min(...data.strengths);
const sMax = Math.max(...data.strengths);
type Beat = { t: number; w: number };
const beats: Beat[] = [];
{
  let last = -9;
  for (let i = 0; i < data.onsets.length; i++) {
    const sn = (data.strengths[i] - sMin) / (sMax - sMin + 1e-9);
    if (sn >= STR_THRESH && data.onsets[i] - last >= MINGAP) {
      beats.push({ t: data.onsets[i], w: WMIN + (1 - WMIN) * Math.pow(sn, GAMMA) });
      last = data.onsets[i];
    }
  }
}
const TWO_SIG2 = 2 * SIGMA * SIGMA;
function env(t: number): number {
  let s = 0;
  for (const b of beats) {
    const dt = t - b.t;
    if (dt > -0.4 && dt < 0.4) s += b.w * Math.exp(-(dt * dt) / TWO_SIG2);
  }
  return s;
}

// ── lyric roller geometry ────────────────────────────────────────────────
const LINE_GAP = 70; // vertical distance between lines
const ROLLER_H = 280; // roller box height
const ROLLER_W = 600; // roller box width
const ANCHOR_Y = 150; // current line's y inside the box
const SHIFT_DUR = 0.4; // seconds the line-roll takes, ENDING at the next line's first word

type Line = { text: string; start: number; end: number };
const lines: Line[] = data.lines as Line[];

export const KamakhyaPlayer: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();
  const t = frame / fps;
  const progress = Math.min(1, frame / (durationInFrames - 1)); // YouTube-style scrubber fill

  // whole-card punch
  const breath = BREATH * (0.5 + 0.5 * Math.sin((2 * Math.PI * t) / BREATH_T));
  const e = env(t);
  const punch = 1 + breath + ZOOM * e;
  const flash = FLASH * e;

  // current fractional lyric position
  let cur = 0;
  for (let i = 0; i < lines.length; i++) {
    if (t >= lines[i].start) cur = i;
    else break;
  }
  // The stack HOLDS on the current line while it's being sung, then rolls up only
  // in the final SHIFT_DUR before the NEXT line's first word starts.
  const nextStart = cur + 1 < lines.length ? lines[cur + 1].start : lines[cur].end + 99;
  const shiftStart = Math.max(lines[cur].start, nextStart - SHIFT_DUR);
  let frac = 0;
  if (t > shiftStart && nextStart > shiftStart) frac = Math.min(1, (t - shiftStart) / (nextStart - shiftStart));
  const eased = frac * frac * (3 - 2 * frac); // smoothstep
  const pos = cur + eased;

  // ── roller visibility: hidden before the first SUNG line, faded out in gaps ──
  const VOCAL_ONSET = 13.0; // lyrics start at ~13s (जय कामाख्या), then your corrections from रण में नाचे
  const GAP_FADE = 0.6; // s to fade out/in around an instrumental gap
  const curEnd = lines[cur].end;
  const nextS2 = cur + 1 < lines.length ? lines[cur + 1].start : curEnd;
  let rollerOp = 1;
  if (t < VOCAL_ONSET) {
    rollerOp = interpolate(t, [VOCAL_ONSET - 0.4, VOCAL_ONSET], [0, 1], {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    });
  } else if (t > curEnd && nextS2 - curEnd > 1.4) {
    // genuine instrumental gap → fade out after the line ends, fade back in before the next
    const outDone = curEnd + GAP_FADE;
    const inStart = nextS2 - GAP_FADE;
    if (t <= outDone) rollerOp = interpolate(t, [curEnd, outDone], [1, 0], { extrapolateRight: "clamp" });
    else if (t >= inStart) rollerOp = interpolate(t, [inStart, nextS2], [0, 1], { extrapolateLeft: "clamp" });
    else rollerOp = 0;
  }

  return (
    <AbsoluteFill style={{ backgroundColor: "#000" }}>
      {/* ── blurred ambient backdrop (same clip, no beat) ── */}
      <AbsoluteFill>
        <OffthreadVideo
          src={BASE_URL}
          muted
          style={{
            width: W,
            height: H,
            objectFit: "cover",
            transform: "scale(1.12)",
            filter: "blur(12px) brightness(0.86) saturate(0.92)",
          }}
        />
      </AbsoluteFill>

      {/* ── curved RECTANGLE ripple waves emanating outward from the card edges (behind card) ── */}
      <AbsoluteFill style={{ justifyContent: "center", alignItems: "center" }}>
        {[0, 0.25, 0.5, 0.75].map((phase, k) => {
          const p = (t * 0.5 + phase) % 1; // 0→1 outward cycle
          const g = p * 170; // how far the wave has expanded beyond the card edge
          const op = (1 - p) * (0.45 + 0.45 * Math.min(1, e * 1.6)); // fade out + beat pump
          return (
            <div
              key={k}
              style={{
                position: "absolute",
                width: CARD_W + g,
                height: CARD_H + g,
                borderRadius: RADIUS + g / 2, // keep the corners curved as it grows
                border: "2px solid rgba(255,212,135,0.85)",
                filter: "blur(5px)",
                opacity: op,
              }}
            />
          );
        })}
      </AbsoluteFill>

      {/* ── the player card (whole-card punch, vector rounded corners + white border + shadow) ── */}
      <AbsoluteFill style={{ justifyContent: "center", alignItems: "center" }}>
        <div
          style={{
            width: CARD_W,
            height: CARD_H,
            transform: `scale(${punch})`,
            position: "relative",
            borderRadius: RADIUS,
            boxShadow: "0 26px 60px 6px rgba(0,0,0,0.62)",
          }}
        >
          {/* clipped video */}
          <div style={{ position: "absolute", inset: 0, borderRadius: RADIUS, overflow: "hidden" }}>
            <OffthreadVideo
              src={BASE_URL}
              muted
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                filter: `brightness(${1 + flash})`,
              }}
            />
          </div>
          {/* white border CENTERED on the edge (half outside, half inside) */}
          <div
            style={{
              position: "absolute",
              left: -BORDER / 2,
              top: -BORDER / 2,
              width: CARD_W + BORDER,
              height: CARD_H + BORDER,
              border: `${BORDER}px solid rgba(255,255,255,0.95)`,
              borderRadius: RADIUS + BORDER / 2,
              boxSizing: "border-box",
              pointerEvents: "none",
            }}
          />
        </div>
      </AbsoluteFill>

      {/* ── channel logo (top-right of card, 20px pad) with beat-pumped BLURRED ripple rings ── */}
      <div
        style={{
          position: "absolute",
          top: CARD_TOP_FROM_EDGE + LOGO_PAD,
          right: CARD_RIGHT_FROM_EDGE + LOGO_PAD,
          width: LOGO,
          height: LOGO,
          pointerEvents: "none",
        }}
      >
        {[0, 0.25, 0.5, 0.75].map((phase, k) => {
          const p = (t * 0.55 + phase) % 1; // steady outward drift
          const size = LOGO * 0.95 + p * 130; // expands well beyond the logo
          const op = (1 - p) * (0.55 + 0.45 * Math.min(1, e * 1.6)); // always visible, pops on beat
          return (
            <div
              key={k}
              style={{
                position: "absolute",
                left: "50%",
                top: "50%",
                width: size,
                height: size,
                marginLeft: -size / 2,
                marginTop: -size / 2,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, transparent 52%, rgba(255,212,135,0.9) 68%, rgba(255,225,170,0.35) 78%, transparent 86%)",
                filter: "blur(5px)",
                opacity: op,
              }}
            />
          );
        })}
        <Img
          src={LOGO_URL}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "contain",
            transform: `scale(${1 + 0.1 * e})`, // gentle beat pulse
            filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.6))",
          }}
        />
      </div>

      {/* ── lyric roller — 3 lines, right-aligned, anchored to the card's bottom-right ── */}
      <div
        style={{
          position: "absolute",
          right: CARD_RIGHT_FROM_EDGE + 40,
          bottom: CARD_BOTTOM_FROM_EDGE + 38, // shifted up 10px

          width: ROLLER_W,
          height: ROLLER_H,
          perspective: 640,
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,1) 28%, rgba(0,0,0,1) 74%, transparent 100%)",
          maskImage:
            "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,1) 28%, rgba(0,0,0,1) 74%, transparent 100%)",
          opacity: rollerOp,
          pointerEvents: "none",
        }}
      >
        {lines.map((ln, i) => {
          const rel = i - pos; // 0 = current line
          if (rel < -1.6 || rel > 1.6) return null;
          const y = ANCHOR_Y + rel * LINE_GAP;
          const ar = Math.abs(rel);
          const opacity = interpolate(ar, [0, 1, 1.6], [1, 0.42, 0], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          });
          const isCur = ar < 0.5;
          const scale = interpolate(ar, [0, 1], [1, 0.9], { extrapolateRight: "clamp" });
          const rotX = interpolate(rel, [-1.6, 0, 1.6], [26, 0, -24]); // drum curve
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                right: 0,
                top: y,
                maxWidth: ROLLER_W,
                textAlign: "right",
                transform: `translateY(-50%) rotateX(${rotX}deg) scale(${scale})`,
                transformOrigin: "right center",
                opacity,
              }}
            >
              <span
                style={{
                  fontFamily: devFont,
                  fontWeight: isCur ? 700 : 500,
                  fontSize: isCur ? 35 : 33,
                  color: "#fff",
                  letterSpacing: 0.3,
                  textShadow: isCur
                    ? "0 2px 18px rgba(0,0,0,0.9), 0 0 26px rgba(255,210,120,0.4)"
                    : "0 2px 12px rgba(0,0,0,0.85)",
                }}
              >
                {ln.text}
              </span>
            </div>
          );
        })}
      </div>

      {/* ── YouTube-style progress bar (STATIC line) + beating triangle playhead ── */}
      <div
        style={{
          position: "absolute",
          left: (W - BAR_W) / 2,
          bottom: BAR_BOTTOM,
          width: BAR_W,
          height: 5,
        }}
      >
        {/* track */}
        <div style={{ position: "absolute", inset: 0, background: "rgba(255,255,255,0.22)", borderRadius: 3 }} />
        {/* fill — no beat, just progress */}
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            height: "100%",
            width: `${progress * 100}%`,
            background: "linear-gradient(90deg,#ffd27a,#fff)",
            borderRadius: 3,
            boxShadow: "0 0 8px rgba(255,210,120,0.55)",
          }}
        />
        {/* play-button playhead — white circle with the triangle HOLED in; pulses on the beat */}
        <div
          style={{
            position: "absolute",
            left: `${progress * 100}%`,
            top: "50%",
            transform: `translate(-50%,-50%) scale(${1 + 0.45 * e})`,
            transformOrigin: "center center",
            width: 30,
            height: 30,
            filter: "drop-shadow(0 0 7px rgba(255,210,120,0.85))",
          }}
        >
          <svg width="30" height="30" viewBox="0 0 100 100">
            {/* solid white disc fully covers the progress line; solid triangle on top = fully visible */}
            <circle cx="50" cy="50" r="49" fill="#ffffff" />
            <path d="M40 31 L72 50 L40 69 Z" fill="#2a1a0e" />
          </svg>
        </div>
      </div>

      {/* ── "Now Playing" title — centred, 3px above the playhead triangle's top, STATIC (no beat) ── */}
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: TITLE_BOTTOM, // 3px above the playhead triangle top, follows the bar
          textAlign: "center",
          fontFamily: devFont,
          fontSize: 18, // smaller than before
          color: "#fff",
          letterSpacing: 0.4,
          whiteSpace: "nowrap",
          textShadow: "0 2px 10px rgba(0,0,0,0.9), 0 0 2px rgba(0,0,0,0.6)",
          pointerEvents: "none",
        }}
      >
        <span style={{ fontWeight: 400, opacity: 0.85 }}>Now Playing: </span>
        <span style={{ fontWeight: 700 }}>Maa Kamakhya vs Narkasur</span>
      </div>

      <Audio src={SONG_URL} />
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
